import { createSelector } from 'reselect';
import { getAveragesSelector } from '@ducks/averages/selectors';
import { toPercentage } from '@/utilities/formatting';
import { getSummary, getAnalysis } from '@/pages/CompanyReport/redux/selectors';

interface Past {
  marketName: string;
  uniqueSymbol: string;
  companyId: string;
  isFund: boolean;
  score: number;
  pastROE: number;
  pastROA: number;
  pastROCE1y: number;
  pastROCE3y: number;
  netIncomeGrowth1Y: number | null;
  companyName: string;
  tickerSymbol: string;
  netIncomeGrowth5Y: number | null;
  pastIndustryROE: number;
  pastIndustryROA: number;
  industryName: string;
  averageMarketGrowth1Y: number;
  averageMarketGrowth5Y: number;
  averageIndustryGrowth1Y: number;
  averageIndustryGrowth5Y: number;
  keyMetric: string;
  keyMetricTitle: string;
  netIncomeGrowth5YFormatted: string;
  averageIndustryGrowth5YFormatted: string;
  averageMarketGrowth5YFormatted: string;
  netIncomeGrowth1YFormatted: string;
  averageIndustryGrowth1YFormatted: string;
  averageMarketGrowth1YFormatted: string;
}

interface KeyMetric {
  value: string;
  title: string;
}

const getPastKeyMetic = (
  netIncomeGrowth5y: number | null,
  revenueGrowth5y: number,
  netIncomeGrowth1y: number | null,
  revenueGrowth1y: number,
  industryName: string,
  averageIndustryGrowth5Y: number
): KeyMetric => {
  if (netIncomeGrowth5y) {
    return {
      value: toPercentage(netIncomeGrowth5y, 1),
      title: 'Historical annual earnings growth',
    };
  } else if (revenueGrowth5y) {
    return {
      value: toPercentage(revenueGrowth5y, 1),
      title: 'Historical annual revenue growth',
    };
  } else if (netIncomeGrowth1y) {
    return {
      value: toPercentage(netIncomeGrowth1y, 1),
      title: 'Last years earnings growth',
    };
  } else if (revenueGrowth1y) {
    return {
      value: toPercentage(revenueGrowth1y, 1),
      title: 'Last years revenue growth',
    };
  } else if (averageIndustryGrowth5Y) {
    return {
      value: toPercentage(averageIndustryGrowth5Y, 1),
      title: `Historical ${industryName} annual earnings growth`,
    };
  }
  return { value: '0', title: '' };
};

const formatter = (value: number | null) =>
  value !== null ? toPercentage(value, 1) : 'n/a';

export const getPastSectionSelector = createSelector(
  [getSummary, getAnalysis, getAveragesSelector],
  (summary, analysis, averages): Past => {
    const { getMarketAverage, getIndustryAverage } = averages;

    const pastKeyMetics = getPastKeyMetic(
      analysis.netIncomeGrowth5y,
      analysis.revenueGrowth5y,
      analysis.netIncomeGrowth1y,
      analysis.revenueGrowth1y,
      summary.industryData.primaryName,
      getIndustryAverage('growthPast5y').value
    );

    const netIncomeGrowth5Y = analysis.netIncomeGrowth5y;
    const netIncomeGrowth1Y = analysis.netIncomeGrowth1y;
    const averageMarketGrowth1Y = getMarketAverage('growthPast1y').value;
    const averageMarketGrowth5Y = getMarketAverage('growthPast5y').value;
    const averageIndustryGrowth1Y = getIndustryAverage('growthPast1y').value;
    const averageIndustryGrowth5Y = getIndustryAverage('growthPast5y').value;

    const netIncomeGrowth5YFormatted = formatter(netIncomeGrowth5Y);
    const averageIndustryGrowth5YFormatted = formatter(averageIndustryGrowth5Y);
    const averageMarketGrowth5YFormatted = formatter(averageMarketGrowth5Y);
    const netIncomeGrowth1YFormatted = formatter(netIncomeGrowth1Y);
    const averageIndustryGrowth1YFormatted = formatter(averageIndustryGrowth1Y);
    const averageMarketGrowth1YFormatted = formatter(averageMarketGrowth1Y);

    return {
      marketName: getIndustryAverage('ROE').source,
      keyMetric: pastKeyMetics.value,
      keyMetricTitle: pastKeyMetics.title,
      score: summary.score.past,
      isFund: summary.isFund,
      pastROA: analysis.pastROA,
      pastROE: analysis.pastROE,
      pastROCE1y: analysis.pastROCE1y,
      pastROCE3y: analysis.pastROCE3y,
      netIncomeGrowth1Y,
      netIncomeGrowth5Y,
      companyId: summary.companyId,
      companyName: summary.shortName,
      tickerSymbol: summary.tickerSymbol,
      uniqueSymbol: summary.uniqueSymbol,
      pastIndustryROE: getIndustryAverage('ROE').value,
      pastIndustryROA: getIndustryAverage('ROA').value,
      industryName: summary.industryData.secondaryName,
      averageMarketGrowth1Y,
      averageMarketGrowth5Y,
      averageIndustryGrowth1Y,
      averageIndustryGrowth5Y,
      netIncomeGrowth5YFormatted,
      averageIndustryGrowth5YFormatted,
      averageMarketGrowth5YFormatted,
      netIncomeGrowth1YFormatted,
      averageIndustryGrowth1YFormatted,
      averageMarketGrowth1YFormatted,
    };
  }
);

export const pastEarningsGrowthPropsFromState = createSelector(
  [getPastSectionSelector, getAveragesSelector],
  (past, averages) => {
    const { getMarketAverage } = averages;
    return {
      companyName: past.companyName,
      marketName: getMarketAverage('netIncomeGrowthAnnual').source,
      industryName: past.industryName,
      netIncomeGrowth1Y: past.netIncomeGrowth1Y,
      netIncomeGrowth5Y: past.netIncomeGrowth5Y,
      averageMarketGrowth1Y: past.averageMarketGrowth1Y,
      averageMarketGrowth5Y: past.averageMarketGrowth5Y,
      averageIndustryGrowth1Y: past.averageIndustryGrowth1Y,
      averageIndustryGrowth5Y: past.averageIndustryGrowth5Y,
      netIncomeGrowth5YFormatted: past.netIncomeGrowth5YFormatted,
      averageIndustryGrowth5YFormatted: past.averageIndustryGrowth5YFormatted,
      averageMarketGrowth5YFormatted: past.averageMarketGrowth5YFormatted,
      netIncomeGrowth1YFormatted: past.netIncomeGrowth1YFormatted,
      averageIndustryGrowth1YFormatted: past.averageIndustryGrowth1YFormatted,
      averageMarketGrowth1YFormatted: past.averageMarketGrowth1YFormatted,
    };
  }
);

export const ROEPropsFromState = createSelector(
  [getPastSectionSelector],
  (past) => {
    return {
      companyROE: past.pastROE,
      companyName: past.companyName,
      industryROE: past.pastIndustryROE,
      industryName: past.industryName,
    };
  }
);

export const ROCEPropsFromState = createSelector(
  [getPastSectionSelector],
  (past) => {
    return {
      companyName: past.companyName,
      ROCE1y: past.pastROCE1y,
      ROCE3y: past.pastROCE3y,
    };
  }
);
